import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import Reveal from "@/components/sites/Reveal";
import { api } from "@/lib/api";
import { useTenantSlug } from "@/lib/brand";

/**
 * The reviews block on an agency's website: its Google rating and latest
 * Google reviews, and the answers its own clients agreed could be quoted.
 *
 * Both halves come from `GET /crm/public/reviews` (crm/reviews_routes.py).
 * Either may be missing, and when both are the block renders nothing at
 * all, the same rule as every other section on these pages: a sparse
 * agency gets a shorter page, never a padded one.
 *
 * GOOGLE'S TERMS, AND WHY THE BLOCK LOOKS AS IT DOES. Reviews are shown in
 * the order Google gives them, unfiltered and unedited, each with the
 * author's name and a link back to Google, under a line saying they are
 * from Google. Long ones are clipped by CSS with the full text a click
 * away, which shortens nothing anybody wrote.
 *
 * `variant` is the site theme: "estate" uses the design pack's `.fs`
 * classes; "classic" the older Tailwind site.
 */
export function useAgencyReviews() {
  const { slug, known } = useTenantSlug();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!known) return undefined;
    let stale = false;
    api.get(`/crm/public/reviews?tenant=${encodeURIComponent(slug)}`)
      .then((r) => { if (!stale) setData(r.data); })
      .catch(() => { if (!stale) setData(null); });
    return () => { stale = true; };
  }, [slug, known]);
  return data;
}

const clip = { display: "-webkit-box", WebkitLineClamp: 7, WebkitBoxOrient: "vertical", overflow: "hidden" };

function items(data) {
  const google = data?.google;
  const fromGoogle = (google?.reviews || []).filter((r) => (r.text || "").trim()).slice(0, 3)
    .map((r) => ({ key: `g-${r.author}-${r.published}`, rating: r.rating, text: r.text, who: r.author,
                   href: r.author_url || google.maps_url, source: "Google" }));
  const ours = (data?.testimonials || []).slice(0, Math.max(0, 6 - fromGoogle.length))
    .map((t, i) => ({ key: `t-${i}`, rating: t.rating, text: t.text, who: t.name, source: "" }));
  return [...fromGoogle, ...ours];
}

export default function AgencyReviews({ variant = "estate" }) {
  const data = useAgencyReviews();
  const google = data?.google;
  const list = items(data);
  if (!data || (!list.length && !(google && google.rating))) return null;

  const summary = google && google.rating ? (
    <a href={google.maps_url || undefined} target="_blank" rel="noreferrer"
       data-testid="google-rating" style={{ color: "inherit" }}>
      {Number(google.rating).toFixed(1)} {"★"} from {google.count} reviews on Google
    </a>
  ) : null;

  if (variant === "classic") {
    return (
      <section className="border-t border-border py-20" data-testid="agency-reviews">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-center">
            <Star className="w-6 h-6 text-accent-ink mx-auto" />
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-6">Reviews</h2>
            {summary && <div className="text-sm mt-3 text-muted-foreground underline underline-offset-4">{summary}</div>}
          </div>
          {list.length > 0 && (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              {list.map((r) => (
                <div key={r.key}>
                  <div className="flex gap-0.5 text-accent-ink" aria-label={`${r.rating} out of 5`}>
                    {Array.from({ length: r.rating || 0 }).map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-current" />)}
                  </div>
                  <blockquote className="font-serif-display text-lg mt-4 leading-relaxed" style={clip}>{"“"}{r.text}{"”"}</blockquote>
                  <div className="text-xs mt-5 text-muted-foreground tracking-widest uppercase">
                    {r.href ? <a href={r.href} target="_blank" rel="noreferrer">{r.who}</a> : r.who}
                    {r.source ? ` · ${r.source}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="section" data-testid="agency-reviews">
      <div className="container">
        <Reveal className="section-head">
          <div>
            <div className="eyebrow">Reviews</div>
            <h2 className="display-l" style={{ marginTop: 14 }}>
              What people <span className="serif-i">say</span>
            </h2>
            {summary && <p style={{ marginTop: 14 }}>{summary}</p>}
          </div>
        </Reveal>
        {list.length > 0 && (
          <div className={list.length === 1 ? "reviews one" : "reviews"}>
            {list.map((r) => (
              <Reveal key={r.key} className="review">
                <div className="stars" aria-label={`${r.rating} out of 5`}>{"★".repeat(r.rating || 0)}</div>
                <p style={{ ...clip, fontSize: 18 }}>{"“"}{r.text}{"”"}</p>
                <div className="who">
                  {r.href ? <a href={r.href} target="_blank" rel="noreferrer" style={{ color: "inherit" }}>{r.who}</a> : r.who}
                  {r.source ? ` · ${r.source}` : ""}
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
